import { h } from 'preact';
import * as BaseComp from 'components/bolt/footer/footer.js';

import Store from 'store/store';
import { geoFromIP } from 'utils/bolt/geo';
import { isInEU } from 'utils/eu';
import { getCopy } from 'utils/copy';
import { getRelativePageUrl } from 'utils/bolt/index';
import { accessNested, joinClasses, sortObjectByOrder } from 'utils/utils';
import { renderCookieConsentLink } from 'utils/cookies';

import Button from 'components/bolt/button/button';
import Link from 'components/bolt/link/link';
import HTML from 'components/core/html/html.js';

import s from 'components/bolt/footer/footer.sss';

export default class Footer extends BaseComp.default {

	constructor(props) {
		super(props);
		this.onInternalLinkClick = this.onInternalLinkClick.bind(this);
	}

	async componentDidMount() {
		super.componentDidMount();
		const geo = await geoFromIP();
		const userInEU = await isInEU(geo?.countryCode);
		this.setState({ userCountry: geo?.countryCode, userInEU });
	}

	getLinkContent(link, type) {
		if (type === 'logo') {
			return super.getLinkContent(link, type);
		}
		// $copy must be a string for getCopy; a malformed entry should render empty
		// rather than crashing the whole footer.
		let copy = typeof link?.$copy === 'string' ? link.$copy : '';
		let content = getCopy(copy, {}, '', null, 'baseCopy') || copy;
		if (type === 'copyright' || type === 'link') {
			content = <HTML content={content} markdown inline />;
		}
		return content;
	}

	// Map CMS page destination to actual page key (same as header does)
	getPageKeyFromDestination(destination) {
		// Map destinations to page keys - matches header's destinationToPageKey
		const destinationMap = {
			tickets: 'boltShowtimes',
			showtimes: 'boltShowtimes',
			homeEnt: 'boltHomeEnt',
			gallery: 'boltGallery',
			videos: 'boltVideos',
			synopsis: 'boltSynopsis',
		};
		return destinationMap[destination] || destination;
	}

	// Handle internal link clicks - same pattern as header uses
	onInternalLinkClick(pageId, e) {
		e.preventDefault();
		Store.emit('CLOSE_AND_NAVIGATE', pageId, e, { instant: false });
	}

	renderLinks(items, type) {
		const { userCountry } = this.state;
		if (userCountry === 'us') {
			items = Object.fromEntries(Object.entries(items || {}).filter(([key, val]) => !val?.nonUsOnly));
		} else {
			items = Object.fromEntries(Object.entries(items || {}).filter(([key, val]) => !val?.usOnly));
		}

		// Get sorted items (same filtering as base class)
		const ageGateEnabled = accessNested(Store.get(), 'appData.options.ageGate', false);
		const isOfAge = accessNested(Store.get(), 'client.ofAge', null);
		const sortedItems = sortObjectByOrder(items, (item, key) => ({ key, ...item })).filter(item => {
			const ageGateShowOnly = +item.ageGateShowOnly || 0;
			if (ageGateShowOnly) {
				if (ageGateShowOnly === 1 && ageGateEnabled && !isOfAge) return false;
				if (ageGateShowOnly === 2 && isOfAge) return false;
			}
			if ((item.excludeFromShowtimes && this.showtimesFooter) || (item.showtimesOnly && !this.showtimesFooter)) {
				return false;
			}
			return true;
		});

		const allPages = this.props.store?.appData?.pages || {};
		const links = sortedItems.map((item, key) => {
			let content = this.getLinkContent(item, type);

			if (item.url === 'cookies') {
				return (
					<li class={s[`${type}Wrapper`]} key={key}>
						{renderCookieConsentLink(content)}
					</li>
				);
			}

			// Handle reserved variable links (@...)
			if (item.url && item.url.startsWith('@')) {
				return (
					<li class={s[`${type}Wrapper`]} key={key}>
						<Button
							class={joinClasses(s[`${type}Link`], this.fontStyles[type === 'copyright' ? 'footerCopyright' : 'footerLegalLinks'])}
							trigger={() => Store.emit('OPEN_RESERVED_VARIABLE_LINK', item.url)}
						>
							{content}
						</Button>
					</li>
				);
			}

			// Check if this is an internal page link - EXACTLY like header does it
			// Header: const page = accessNested(this.props, ['appData', 'pages', pageId]);
			// Header: const isInternalLink = !!page || element.path?.startsWith('@');

			// Strip leading slash from URL if present (CMS may store /synopsis but pages use synopsis)
			let destination = item.url;
			if (destination && destination.startsWith('/') && !destination.startsWith('//')) {
				destination = destination.slice(1);
			}

			// Map destination to actual page key (synopsis -> boltSynopsis, etc.)
			const pageKey = this.getPageKeyFromDestination(destination);
			const page = allPages[pageKey];
			const isExternalUrl = /^(https?:)?\/\//.test(item.url);
			const isInternalLink = !!page || item.url?.startsWith('@');

			// Handle internal page links with onClick navigation (same as header)
			if (isInternalLink && !isExternalUrl) {
				return (
					<li class={s[`${type}Wrapper`]} key={key}>
						<Link
							href={getRelativePageUrl(pageKey) || '#'}
							class={joinClasses(s[`${type}Link`], this.fontStyles[type === 'copyright' ? 'footerCopyright' : 'footerLegalLinks'])}
							onClick={e => this.onInternalLinkClick(pageKey, e)}
						>
							{content}
						</Link>
					</li>
				);
			}

			// Handle external links
			let Element = item.url ? Link : 'div';
			let attributes = {
				class: joinClasses(s[`${type}Link`], this.fontStyles[type === 'copyright' ? 'footerCopyright' : 'footerLegalLinks']),
				ariaLabel: item.ariaLabel,
				title: item.title,
			};
			if (item.url) {
				const disableNoOpNoRef = accessNested(this.props, 'data.defaultFooter.disableNoOpenerNoReferrer', false);
				attributes.href = item.url;
				attributes.target = item.sameWindow ? '_self' : (item.target || (isExternalUrl ? '_blank' : '_self'));
				attributes.rel = !disableNoOpNoRef && isExternalUrl && !item.sameWindow ? 'noopener noreferrer' : null;
				attributes.onClick = e => this.onLinkClick(item, key, e);
			}

			return (
				<li class={s[`${type}Wrapper`]} key={key}>
					<Element {...attributes}>{content}</Element>
				</li>
			);
		});

		return links;
	}
}